import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { TableContainer, Typography, Box, ThemeProvider, Button, TextField, Tooltip, IconButton } from "@mui/material";
import { VariableSizeList } from "react-window";
import { IniInfo } from "system/types/interfaces";
import { usePolicyEditorPermissions } from "system/hooks";
import { RootState } from "system/store";
import { useDispatch, useSelector } from "react-redux";
import { policyEditorActions } from "system/store/slices/policy-editor-slice";
import { muiTheme } from "system/styles/muiTheme";
import { Icon } from "@fluentui/react/lib/Icon";
import { ActiveDirectoryOURow } from "./ActiveDirectoryOURow";
import { ActiveDirectoryOUModal } from "./ActiveDirectoryOUModal";
import PendingChangesModal from "../applicabilityRulesTab/PendingChangesModal";
import { useDebouncedCallback } from "use-debounce";
import { isRowDirty, restoreOriginalCommentFields, restoreOriginalUncommentFields } from "components/modules/policy-editor/utils/ruleSignature";

const ActiveDirectoryOUTab = () => {
  const dispatch = useDispatch();
  const { isReadOnly: readOnlyViewer } = usePolicyEditorPermissions();
  const ous = useSelector((state: RootState) => state.policyEditor.activeDirectoryOUs);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [draftOU, setDraftOU] = useState<IniInfo | null>(null);
  const [draftComment, setDraftComment] = useState<IniInfo | null>(null);
  const [isPendingChangesModalOpen, setPendingChangesModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<VariableSizeList>(null);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  // Regla por defecto
  useEffect(() => {
    if (ous.length === 0) {
      dispatch(
        policyEditorActions.addADOU({
          index: 0,
          config: {
            id: "default-ou",
            name: '""',
            value: "any endpoint",
            delimiter: "is for",
            pendingChanges: false,
            isDeleted: false,
          },
        })
      );
    }
  }, [ous.length, dispatch]);

  const handleSearch = useDebouncedCallback((term: string) => {
    if (!term) {
      setSearchResults([]);
      setCurrentResultIndex(-1);
      return;
    }
    const results = ous
      .map((ou, index) => {
        const isComment = ou.isComment ?? false;
        const searchInName = ou.name.toLowerCase().includes(term.toLowerCase());
        const searchInComment = isComment && ou.value && ou.value.toLowerCase().includes(term.toLowerCase());
        const searchInValue =
          !isComment &&
          `${ou.delimiter} ${ou.value}${ou.inlineComment ? `;${ou.inlineComment}` : ""}`.toLowerCase().includes(term.toLowerCase());
        return searchInName || searchInComment || searchInValue ? index : -1;
      })
      .filter((index) => index !== -1);
    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);
    if (results.length > 0) {
      listRef.current?.scrollToItem(results[0], "center");
    }
  }, 800);

  useEffect(() => {
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.querySelector("input")?.focus();
      }
    }, 1000);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (searchResults.length === 0) return;
      switch (e.key) {
        case "Enter":
          e.preventDefault();
          const nextIndex = (currentResultIndex + 1) % searchResults.length;
          setCurrentResultIndex(nextIndex);
          listRef.current?.scrollToItem(searchResults[nextIndex], "center");
          break;
        case "ArrowUp":
          e.preventDefault();
          const prevIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
          setCurrentResultIndex(prevIndex);
          listRef.current?.scrollToItem(searchResults[prevIndex], "center");
          break;
        case "ArrowDown":
          e.preventDefault();
          const nextIndexDown = (currentResultIndex + 1) % searchResults.length;
          setCurrentResultIndex(nextIndexDown);
          listRef.current?.scrollToItem(searchResults[nextIndexDown], "center");
          break;
      }
    },
    [currentResultIndex, searchResults]
  );

  const handleReorderRows = useCallback(
    (oldIndex: number, newIndex: number) => {
      if (oldIndex === newIndex) return;
      const adjustedNewIndex = Math.max(0, Math.min(newIndex, ous.length - 1));
      dispatch(policyEditorActions.reorderADOUs({ oldIndex, newIndex: adjustedNewIndex }));
      listRef.current?.scrollToItem(adjustedNewIndex, "center");
    },
    [dispatch, ous.length]
  );

  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;
      if (active.id !== over.id) {
        const oldIndex = ous.findIndex((d) => d.id === active.id);
        const newIndex = ous.findIndex((d) => d.id === over.id);
        handleReorderRows(oldIndex, newIndex);
      }
    },
    [handleReorderRows, ous]
  );

  const handleAddLineClick = useCallback((index: number) => {
    setDraftOU({
      id: Date.now().toString(),
      name: "",
      value: "",
      delimiter: "is for",
      pendingChanges: true,
    });
    setInsertIndex(index);
    setModalOpen(true);
    setModalMode("add");
  }, []);

  const handleModalClose = useCallback(() => {
    setModalMode("add");
    setInsertIndex(null);
    setModalOpen(false);
    setDraftOU(null);
    setDraftComment(null);
  }, []);

  const handleEditActiveDirectoryOUs = useCallback((domain: IniInfo, index: number, isComment: boolean = false) => {
    if (isComment) {
      setDraftComment(domain);
      setDraftOU(null);
    } else {
      setDraftOU(domain);
      setDraftComment(null);
    }
    setInsertIndex(index);
    setModalOpen(true);
    setModalMode("edit");
  }, []);

  const handleToggleComment = useCallback(
    (id: string, newContent: string, isComment: boolean) => {
      const updatedConfigs = ous.map((config: IniInfo) => {
        if (config.id !== id) return config;
        if (isComment) {
          const restored = restoreOriginalCommentFields(config);
          if (restored) return restored;
          return {
            ...config,
            isComment: true,
            value: newContent,
            name: `New-Comment-${Date.now()}`,
            delimiter: "",
            inlineComment: config.inlineComment ?? "",
          };
        } else {
          // Uncommenting: if the row was originally NOT a comment, restore the load-time fields
          // so the signature matches the original and the row drops out of pendingChanges. AB#3115.
          const restored = restoreOriginalUncommentFields(config);
          if (restored) return restored;
          // Otherwise (line added during this session) parse rule.value back into fields.
          const uncommentedContent = config.value?.replace(/^;/, "").trim() || "";
          const validDelimiters = [" is for any ", " is ", " = "];
          let name = uncommentedContent;
          let delimiter = "";
          let value = "";
          let inlineComment = "";

          for (const d of validDelimiters) {
            const escapedD = d.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const parts = uncommentedContent.split(new RegExp(`(?:^|\\s)${escapedD}(?:\\s|$)`, "i"));
            if (parts.length >= 2) {
              name = parts[0].trim();
              delimiter = d;
              const afterDelimiter = parts.slice(1).join(d).trim();
              const commentParts = afterDelimiter.split(/;(.+)/);
              if (commentParts.length > 1) {
                value = commentParts[0].trim();
                inlineComment = commentParts[1].trim();
              } else {
                value = afterDelimiter;
                inlineComment = "";
              }
              break;
            }
          }

          if (!delimiter) {
            name = uncommentedContent;
            const commentParts = uncommentedContent.split(/;(.+)/);
            if (commentParts.length > 1) {
              name = commentParts[0].trim();
              inlineComment = commentParts[1].trim();
            }
          }

          return {
            ...config,
            isComment: false,
            name,
            delimiter,
            value,
            inlineComment,
          };
        }
      });
      const withDirt = updatedConfigs.map((c) =>
        c.id === id ? { ...c, pendingChanges: isRowDirty(c, updatedConfigs) } : c
      );
      dispatch(policyEditorActions.setActiveDirectoryOUs(withDirt));
    },
    [ous, dispatch]
  );

  const handleModalSubmit = useCallback(
    (data: IniInfo) => {
      if (insertIndex === null) return;

      const existingIndex = ous.findIndex((x) => x.id === data.id);

      if (existingIndex >= 0) {
        dispatch(policyEditorActions.updateADOU({ index: existingIndex, updatedConfig: data }));
      } else {
        const insertPosition = insertIndex ?? ous.length;
        dispatch(policyEditorActions.addADOU({ index: insertPosition, config: data }));
      }

      handleModalClose();
    },
    [insertIndex, ous, handleModalClose, dispatch]
  );

  const handleDeleteRow = useCallback(
    (index: number) => {
      dispatch(policyEditorActions.deleteADOU(index));
    },
    [dispatch]
  );

  const handleUndoDelete = useCallback(
    (index: number) => {
      dispatch(policyEditorActions.undoDeleteADOU(index));
    },
    [dispatch]
  );

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "/") {
        e.preventDefault();
        searchInputRef.current?.querySelector("input")?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  const modifiedOUs = useMemo(() => {
    return ous.map((rule, index) => ({ index, rule })).filter(({ rule }) => rule.pendingChanges);
  }, [ous]);

  const Row = React.memo(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const ou = ous[index];
    return (
      <ActiveDirectoryOURow
        key={ou.id}
        id={ou.id!}
        ou={ou}
        index={index}
        style={style}
        onAdd={handleAddLineClick}
        onDelete={handleDeleteRow}
        onUndoDelete={handleUndoDelete}
        onEdit={handleEditActiveDirectoryOUs}
        searchTerm={searchTerm}
        onReorder={handleReorderRows}
        totalRows={ous.length}
        onToggleComment={handleToggleComment}
        readOnlyViewer={readOnlyViewer}
      />
    );
  });

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "text.primary" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={1} alignItems="center">
            <Tooltip title="Press Enter to navigate through the results" arrow>
              <TextField
                ref={searchInputRef}
                placeholder="Search OUs..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleSearch(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                sx={{ width: "350px" }}
                InputProps={{
                  endAdornment: searchResults.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          padding: "0 8px",
                          color: "#fff",
                          opacity: 0.7,
                          borderRight: "1px solid #cccccc",
                        }}
                      >
                        {currentResultIndex + 1}/{searchResults.length}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const prev = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
                          setCurrentResultIndex(prev);
                          listRef.current?.scrollToItem(searchResults[prev], "center");
                        }}
                        sx={{ color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                      >
                        <Icon iconName="ChevronUp" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const next = (currentResultIndex + 1) % searchResults.length;
                          setCurrentResultIndex(next);
                          listRef.current?.scrollToItem(searchResults[next], "center");
                        }}
                        sx={{ color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                      >
                        <Icon iconName="ChevronDown" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchTerm("");
                          handleSearch("");
                        }}
                        sx={{ fontSize: "18px", color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                      >
                        <Icon iconName="Cancel" style={{ fontSize: "inherit" }} />
                      </IconButton>
                    </Box>
                  ),
                }}
              />
            </Tooltip>
          </Box>

          <Box display="flex" gap={1} alignItems="center">
            {modifiedOUs.length > 0 && (
              <Button
                variant="text"
                sx={{
                  backgroundColor: "#fbc02d",
                  color: "#000",
                  "&:hover": { backgroundColor: "#e3ac27" },
                  textTransform: "none",
                }}
                onClick={() => setPendingChangesModalOpen(true)}
              >
                <b style={{ marginRight: "3px" }}>{modifiedOUs.length}</b> Pending {modifiedOUs.length === 1 ? "Change" : "Changes"}
              </Button>
            )}
          </Box>
        </Box>

        <TableContainer
          sx={{
            maxHeight: "85vh",
            border: "1px solid #333",
            backgroundColor: "#1b1a19",
            borderRadius: "8px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
          component="div"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={() => {
              setSearchTerm("");
              setSearchResults([]);
              setCurrentResultIndex(-1);
            }}
          >
            <SortableContext items={ous.map((d) => d.id || d.name)} strategy={verticalListSortingStrategy}>
              <VariableSizeList height={window.innerHeight - 200} width="100%" itemCount={ous.length} itemSize={() => 50} ref={listRef}>
                {Row}
              </VariableSizeList>
            </SortableContext>
          </DndContext>
        </TableContainer>
      </Box>

      <ActiveDirectoryOUModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        initialOU={draftOU}
        initialComment={draftComment}
        mode={modalMode}
      />

      <PendingChangesModal
        open={isPendingChangesModalOpen}
        onClose={() => setPendingChangesModalOpen(false)}
        modifiedRules={modifiedOUs}
        onNavigate={handleEditActiveDirectoryOUs}
      />
    </ThemeProvider>
  );
};

export default ActiveDirectoryOUTab;
